import { useState } from 'react'
import { useForm } from '@inertiajs/react'
import {
  Button,
  Card,
  CardTitle,
  Dialog,
  DialogTitle,
  DialogBody,
  DialogActions,
  Field,
  Label,
  Input,
  Textarea,
  ErrorMessage,
  Text,
} from '@/components/ui'
import { useTranslations } from '@/lib/i18n'
import { formatMoney } from '@/lib/format'
import type { PaymentRecord } from '@/types'

type Op = 'refund' | 'capture' | 'reverse'
interface Eligible {
  key: Op
  remaining: string
  color: 'brand' | 'accent'
}

/**
 * Refund / capture / reverse actions on a payment. Buttons appear only for
 * eligible operations (the server re-checks live before acting). Each opens a
 * confirm dialog with an optional partial amount + VAT + comment.
 */
export function PaymentActions({ payment }: { payment: PaymentRecord }) {
  const { t, locale } = useTranslations()
  const [active, setActive] = useState<Eligible | null>(null)

  const eligible: Eligible[] = [
    payment.refundable && { key: 'refund', remaining: payment.refundable_remaining, color: 'accent' },
    payment.capturable && { key: 'capture', remaining: payment.capturable_remaining, color: 'brand' },
    payment.reversible && { key: 'reverse', remaining: payment.reversible_remaining, color: 'brand' },
  ].filter(Boolean) as Eligible[]

  if (eligible.length === 0) return null

  return (
    <Card>
      <CardTitle>{t('actions.title')}</CardTitle>
      <div className="mt-4 flex flex-wrap gap-2">
        {eligible.map((op) => (
          <Button key={op.key} color={op.color} onClick={() => setActive(op)}>
            {t(`actions.${op.key}.button`)}
          </Button>
        ))}
      </div>

      {active && (
        <ActionDialog
          operation={active}
          transactionId={payment.transaction_id}
          currency={payment.currency}
          locale={locale}
          t={t}
          onClose={() => setActive(null)}
        />
      )}
    </Card>
  )
}

function ActionDialog({
  operation,
  transactionId,
  currency,
  locale,
  t,
  onClose,
}: {
  operation: Eligible
  transactionId: string
  currency: string
  locale: string
  t: (key: string, vars?: Record<string, string | number>) => string
  onClose: () => void
}) {
  const form = useForm({ amount: '', vat: '', comment: '' })

  function submit(event: React.FormEvent) {
    event.preventDefault()
    form.transform((data) => ({ action_request: data }))
    form.post(`/payments/${transactionId}/${operation.key}`, {
      preserveScroll: true,
      onSuccess: onClose,
    })
  }

  return (
    <Dialog open onClose={onClose}>
      <DialogTitle>{t(`actions.${operation.key}.title`)}</DialogTitle>
      <DialogBody>
        <form id="action-form" onSubmit={submit} className="flex flex-col gap-4">
          <Field>
            <Label>{t('actions.amount')}</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              inputMode="decimal"
              value={form.data.amount}
              onChange={(e) => form.setData('amount', e.target.value)}
              placeholder={formatMoney(operation.remaining, currency, locale)}
            />
            <Text className="mt-1 text-xs text-text/60">
              {t('actions.full_hint', { remaining: formatMoney(operation.remaining, currency, locale) })}
            </Text>
            {form.errors.amount && <ErrorMessage>{form.errors.amount}</ErrorMessage>}
          </Field>

          <Field>
            <Label>{t('actions.vat')}</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              inputMode="decimal"
              value={form.data.vat}
              onChange={(e) => form.setData('vat', e.target.value)}
            />
          </Field>

          <Field>
            <Label>{t('actions.comment')}</Label>
            <Textarea value={form.data.comment} onChange={(e) => form.setData('comment', e.target.value)} rows={2} />
          </Field>
        </form>
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" form="action-form" color={operation.color} disabled={form.processing}>
          {t(`actions.${operation.key}.button`)}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
